
import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { Recipe } from '@/types/recipe';
import { MealCompositionWithRecipes } from '@/types/mealComposition';
import { MealType } from '@/types/mealPlan';
import { mealPlanService } from '@/services/mealPlan';
import { useCurrentUserFamily } from '@/hooks/useCurrentUserFamily';
import { useUser } from '@/contexts/UserContext';
import { toast } from 'sonner';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MEAL_PLANS } from '@/constants/queryKeys';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import ImageSelectionGrid from '@/components/meal-planner/ImageSelectionGrid';
import PageLayout from '@/components/layout/PageLayout';

type SelectedItem = 
  | { type: 'recipe'; data: Recipe } 
  | { type: 'composition'; data: MealCompositionWithRecipes } 
  | null;

export default function MealSelection() {
  const { date } = useParams<{ date: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { familyId } = useCurrentUserFamily();
  const { isAuthenticated } = useUser();
  const [searchParams] = useSearchParams();
  
  // Get meal type from URL params or default to dinner
  const initialMealType = (searchParams.get('mealType') as MealType) || 'dinner';
  const [selectedMealType, setSelectedMealType] = useState<MealType>(initialMealType);

  if (!date) {
    navigate('/meal-planner');
    return null;
  }

  const parsedDate = parseISO(date);
  const formattedDate = format(parsedDate, 'EEEE, MMM d');

  const saveMealPlanMutation = useMutation({
    mutationFn: (variables: { 
      recipeId: string | null; 
      compositionId?: string | null;
      mealType: MealType;
    }) => {
      if (!familyId) throw new Error("Family ID not found");
      
      if (variables.compositionId) {
        return mealPlanService.saveMealPlanComposition(date, variables.compositionId, familyId, variables.mealType);
      }
      return mealPlanService.saveMealPlan(date, variables.recipeId, familyId, variables.mealType);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MEAL_PLANS] });
      toast.success(`${selectedMealType.charAt(0).toUpperCase() + selectedMealType.slice(1)} plan updated successfully!`);
      navigate('/meal-planner');
    },
    onError: (error: Error) => {
      toast.error(`Failed to save meal plan: ${error.message}`);
    },
  });

  const handleSelect = (item: SelectedItem) => {
    if (item?.type === 'recipe') {
      saveMealPlanMutation.mutate({ 
        recipeId: item.data.id, 
        compositionId: null, 
        mealType: selectedMealType 
      });
    } else if (item?.type === 'composition') {
      saveMealPlanMutation.mutate({ 
        recipeId: null, 
        compositionId: item.data.id, 
        mealType: selectedMealType 
      });
    } else {
      saveMealPlanMutation.mutate({ 
        recipeId: null, 
        compositionId: null, 
        mealType: selectedMealType 
      });
    }
  };

  return (
    <PageLayout title={`Select ${selectedMealType} for ${formattedDate}`}>
      <div className="py-6 max-w-6xl mx-auto">
        <ImageSelectionGrid
          onSelect={handleSelect}
          isSubmitting={saveMealPlanMutation.isPending}
        />
      </div>
    </PageLayout>
  );
}
